import { TableBadge } from "@/components/ui/TableBadge/TableBadge";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useLayoutEffect, useRef, useState } from "react";

const claimBadgeClassName = "border-border bg-muted/50 text-foreground";

export function ClaimTypesCell({
  claimTypes,
}: {
  claimTypes?: readonly { id: string; label: string }[];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;
    const trigger = triggerRef.current;
    if (!container || !measurement || !trigger || !claimTypes?.length) return;

    const count = claimTypes.length;
    const measure = () => {
      const availableWidth = container.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(trigger).columnGap) || 0;
      const widths = Array.from(
        measurement.children,
        (child) => child.getBoundingClientRect().width,
      );
      let usedWidth = 0;
      let fittingCount = 0;
      for (let index = 0; index < count; index += 1) {
        usedWidth += widths[index] + (index > 0 ? gap : 0);
        const remaining = count - index - 1;
        const totalWidth =
          usedWidth + (remaining > 0 ? gap + widths[count + remaining - 1] : 0);
        if (totalWidth <= availableWidth) fittingCount = index + 1;
      }
      setVisibleCount(fittingCount);
    };

    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => {
        window.removeEventListener("resize", measure);
      };
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    for (const child of Array.from(measurement.children))
      observer.observe(child);
    return () => {
      observer.disconnect();
    };
  }, [claimTypes]);

  if (!claimTypes?.length) return "-";

  const shownCount = Math.min(visibleCount, claimTypes.length);
  const hiddenCount = claimTypes.length - shownCount;
  return (
    <div
      ref={containerRef}
      className="relative w-full min-w-0 overflow-hidden [contain:inline-size]"
    >
      <div
        ref={measurementRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute top-0 left-0 flex w-max gap-1"
      >
        {claimTypes.map((claim) => (
          <TableBadge key={claim.id} className={claimBadgeClassName}>
            {claim.label}
          </TableBadge>
        ))}
        {claimTypes.map((claim, index) => (
          <TableBadge key={`count-${claim.id}`} className={claimBadgeClassName}>
            +{index + 1}
          </TableBadge>
        ))}
      </div>
      <TooltipWrapper
        message={
          <span className="block space-y-1">
            {claimTypes.map((claim) => (
              <span key={claim.id} className="block">
                {claim.label}
              </span>
            ))}
          </span>
        }
      >
        <button
          ref={triggerRef}
          type="button"
          aria-label={`Claim types: ${claimTypes.map((claim) => claim.label).join(", ")}`}
          className="focus-visible:ring-ring flex min-h-[22px] w-full min-w-0 items-center gap-1 overflow-hidden rounded-lg text-left focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ")
              event.stopPropagation();
          }}
        >
          {claimTypes.slice(0, shownCount).map((claim) => (
            <TableBadge key={claim.id} className={claimBadgeClassName}>
              {claim.label}
            </TableBadge>
          ))}
          {hiddenCount > 0 && (
            <TableBadge truncate className={claimBadgeClassName}>
              +{hiddenCount}
            </TableBadge>
          )}
        </button>
      </TooltipWrapper>
    </div>
  );
}
